<section class="login-page">
  <aside class="auth-pane auth-pane--left">
    <div class="auth-left-inner">
      <div class="brand">
        <button type="button" class="back-arrow" (click)="goBack()" aria-label="Go back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <img src="assets/branding/logo-navy-1024.png" alt="LaunchPocket" class="brand-logo" />

        <nav class="brand-nav" aria-label="Site">
          <a routerLink="/" class="brand-nav__link">Home</a>
          <a routerLink="/product" class="brand-nav__link">Product</a>
          <a routerLink="/pricing" class="brand-nav__link">Pricing</a>
          <a routerLink="/contact" class="brand-nav__link">Contact</a>
        </nav>
      </div>

      <article class="login-container">
        <h1>Welcome back</h1>
        <p class="subtitle subtitle--primary">Sign Up / Sign In using Google Login</p>

        <div id="google-signin-btn" class="google-slot" [class.disabled]="loading()"></div>

        @if (!passwordMode()) {
          <div class="email-login">
            <p class="or-divider" aria-hidden="true">OR</p>
            <input
              type="email"
              placeholder="you@company.com"
              [disabled]="loading()"
              [(ngModel)]="emailValue"
            />
            <button type="button" (click)="submitEmail()" [disabled]="loading()">Continue</button>
            <button
              type="button"
              class="email-login__secondary"
              (click)="sendSignupLink()"
              [disabled]="loading()"
            >
              Send signup link
            </button>
            <p class="signup-hint">
              Enter your email and continue to sign in with your password.<br />
              New here? Send a signup link — we’ll email instructions if an account can be created.
            </p>
          </div>
        } @else {
          <div class="password-inline">
            <div class="password-inline__header">
              <p class="subtitle subtitle--primary">Sign in as {{ emailValue.trim() }}</p>
              <button
                type="button"
                class="icon-button"
                (click)="changeEmail()"
                [disabled]="loading()"
                aria-label="Change email"
                title="Change email"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path
                    d="M4 20h4l10.5-10.5a1.5 1.5 0 0 0 0-2.1l-1.9-1.9a1.5 1.5 0 0 0-2.1 0L4 16v4z"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M13.5 6.5l4 4"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div class="password-login">
              <label for="password">Password</label>
              <div class="password-input-wrap">
                <input
                  id="password"
                  [type]="passwordVisible() ? 'text' : 'password'"
                  [(ngModel)]="passwordValue"
                  [disabled]="loading()"
                  autocomplete="current-password"
                />
                <app-password-visibility-toggle
                  [visible]="passwordVisible()"
                  [disabled]="loading()"
                  (toggled)="togglePasswordVisibility()"
                />
              </div>
              @if (passwordError()) {
                <p class="error">{{ passwordError() }}</p>
              }
              <p class="signup-hint" style="margin-top: 10px;">
                <a routerLink="/forgot-password">Forgot password?</a>
              </p>
              <button
                type="button"
                class="password-login__submit"
                (click)="submitPassword()"
                [disabled]="loading()"
              >
                {{ loading() ? 'Signing in...' : 'Sign in' }}
              </button>
              <p class="signup-hint">Prefer Google? Use the Google login button above.</p>
            </div>
          </div>
        }

        @if (loading()) {
          <app-inline-loading label="Validating access..." />
        }

        @if (message()) {
          <p class="info info--success">{{ message() }}</p>
        }

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }

      </article>

      <footer class="auth-footer">
        <span>© 2025 LaunchPocket</span>
        <nav>
          <a routerLink="/contact">Help</a>
          <a routerLink="/privacy">Privacy</a>
          <a routerLink="/terms">Terms</a>
        </nav>
      </footer>
    </div>
  </aside>

  <main class="auth-pane auth-pane--right" aria-hidden="true">
    <div class="visual-shell">
      <div class="visual-card">
        <p class="visual-badge">INTELLIGENCE ACTIVE</p>
        <h2>The intelligence layer for <span>Shopify stores.</span></h2>
        <p class="visual-copy">
          LaunchPocket turns your store data into clear insights across operations, support, and customer feedback so
          you can take informed actions.
        </p>
        <div class="visual-metrics">
          <article class="metric-card">
            <span class="metric-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4 17L9 12L13 15L20 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                <circle cx="9" cy="12" r="1.5" fill="currentColor"></circle>
                <circle cx="13" cy="15" r="1.5" fill="currentColor"></circle>
                <circle cx="20" cy="8" r="1.5" fill="currentColor"></circle>
              </svg>
            </span>
            <h3>Operational Insights</h3>
            <p>Understand what's happening across your store and identify areas that need attention.</p>
          </article>
          <article class="metric-card">
            <span class="metric-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <rect x="3.5" y="4.5" width="17" height="3.2" rx="1.2" stroke="currentColor" stroke-width="1.8"></rect>
                <rect x="3.5" y="10.4" width="17" height="3.2" rx="1.2" stroke="currentColor" stroke-width="1.8"></rect>
                <rect x="3.5" y="16.3" width="17" height="3.2" rx="1.2" stroke="currentColor" stroke-width="1.8"></rect>
              </svg>
            </span>
            <h3>Product &amp; Review Insights</h3>
            <p>Use customer feedback and product data to improve listings and customer experience.</p>
          </article>
        </div>
      </div>
      <div class="visual-tabs">
        <span>Merchant</span>
        <span>Analytics</span>
        <span>CoreFlow</span>
      </div>
    </div>
  </main>
  <app-security-check-modal
    [open]="securityCheckOpen()"
    (verified)="onTurnstileVerified($event)"
    (cancelled)="onSecurityCheckCancelled()"
  />
</section>
